//
// ProjectMembersWorksheet
//
// Project members worksheet

import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { useMousePopover } from '../../hooks/useMousePopover'
import { useTooltipPopover } from '../../hooks/useTooltipPopover'
import { useStore } from '../../stores'

import { HView, Spacer, VView } from '../../appview'
import { Header, Icon, Item, Property } from '../../components'
import { ProjectMemberMenu } from '../../menus'
import { InviteMemberPanel, ProjectMember } from '../../panels'
import { sortProjectMembers } from '../../stores/data/utils'

export const ProjectMembersWorksheet = observer(function ProjectMembersWorksheet({
    project,
}) {
    const { app, data, view } = useStore()

    const is_owner = view.user && view.project && view.user.gid === view.project.owner

    const [selectedMember, setSelectedMember] = useState(null)

    const onResendInvite = user_gid => {
        view.project.resendInvite(user_gid)
    }

    const onDeactivateMember = user_gid => {
        view.project.deactivateMember(user_gid)
    }

    const onReactivateMember = user_gid => {
        view.project.reactivateMember(user_gid)
    }

    const onRemoveMember = user_gid => {
        view.project.removeMember(user_gid)
    }

    // ContextMenus and Popovers
    const ProjectMemberContextMenu = useMousePopover(ProjectMemberMenu)
    const InviteMemberPopover = useTooltipPopover(InviteMemberPanel)

    const onShowProjectMemberContextMenu = (e, member) => {
        if (!is_owner) return
        if (member.roles.includes('owner')) return
        setSelectedMember(member)
        ProjectMemberContextMenu.onShow(e)
    }

    const onShowInviteMemberPopover = e => {
        if (!is_owner) return
        InviteMemberPopover.onShow(e)
    }

    const onDoneInviteMember = () => {
        InviteMemberPopover.hide()
    }

    const onCancelInviteMember = () => {
        InviteMemberPopover.hide()
    }

    const onProjectMemberAction = (user_gid, action) => {
        const actions = {
            resend_invite: onResendInvite,
            deactivate: onDeactivateMember,
            reactivate: onReactivateMember,
            remove: onRemoveMember,
        }
        if (!(action in actions)) {
            console.log(
                `onProjectMemberAction: unhandled action '${action}' on '${user_gid}'`
            )
            return
        }
        ProjectMemberContextMenu.hide()
        actions[action](user_gid)
    }

    const project_members = data.membersForProject(project.gid)
    sortProjectMembers(project_members, data.users)

    const ProjectMembers = project_members
        .filter(project_member => {
            if (project_member.roles.includes('api')) {
                return false
            }
            if (
                !is_owner &&
                view.user &&
                view.user.gid !== project_member.user_gid &&
                ['inactive', 'invited'].includes(project_member.status)
            ) {
                return false
            }
            return true
        })
        .map(project_member => (
            <ProjectMember
                key={project_member.user_gid}
                member={project_member}
                onClick={e => onShowProjectMemberContextMenu(e, project_member)}
                onContextMenu={e => onShowProjectMemberContextMenu(e, project_member)}
            />
        ))

    return (
        <VView className="worksheet worksheet-settings worksheet-project-members">
            <HView>
                <VView grow>
                    <Header>{app.text('Project members')}</Header>
                    <ProjectMemberContextMenu.Panel
                        member={selectedMember}
                        onAction={onProjectMemberAction}
                    />
                    <InviteMemberPopover.Panel
                        onDone={onDoneInviteMember}
                        onCancel={onCancelInviteMember}
                    />
                    <Property label={app.text('members')}>
                        {ProjectMembers}
                        {is_owner ? (
                            <Item
                                onClick={onShowInviteMemberPopover}
                                style={{ marginTop: 5 }}
                                ref={InviteMemberPopover.anchorRef}
                            >
                                <Icon className="itemtype" name="plus" size="text" />
                                <span className="cc-dimmed">
                                    {app.text('Invite a new member')}
                                </span>
                            </Item>
                        ) : undefined}
                    </Property>
                </VView>
                <Spacer size={20} />
                <VView grow></VView>
            </HView>
        </VView>
    )
})
